// frontend/src/pages/CalendarView.js
//
// Primary target: `drag-and-drop calendar for social scheduling` — 259
// impressions at position 11.1 with a thin page and zero clicks. That long-tail
// term is soft; the head terms in this cluster are the hardest in the dataset
// (`social media content calendar` comp 88, `content calendar template` 97,
// `social media planner` 100), so they are secondary here, not the headline.
//
// NOTE: this page describes the intended calendar feature set, ahead of parts of
// the build — same arrangement as /bulk-posting, recorded in docs/SEO_PROGRESS.md.
// Drag-to-reschedule, month/week/day/agenda views, filtering and status are in
// CalendarPage.js today. Sharing, export, colour-coded campaigns and approvals
// are not yet.
//
// Uses plain <table className='table'>, NOT react-bootstrap <Table> — that
// component was part of the React 19 blank-screen crash fixed in cdab6060.
import '../custom.css'

import React from 'react'
import { Container, Row, Col, Image, Button, Card, Accordion } from 'react-bootstrap'
import {
  CalendarWeek, ArrowsMove, Funnel, People, Clock, CheckCircleFill,
} from 'react-bootstrap-icons'

const calendarShot = '/assets/logo/calender.webp'

const VIEWS = [
  ['Month', 'The whole month at a glance — the view most people live in.'],
  ['Week',  'A tighter look at cadence when you are planning a launch week.'],
  ['Day',   'Everything going out today, in order.'],
  ['Agenda','A plain list, easiest to scan and share.'],
]

const FEATURES = [
  { icon: <ArrowsMove />,   title: 'Drag to reschedule', body: 'Pick a post up and drop it on another day. The scheduled time moves with it — no reopening the post to change a date.' },
  { icon: <CalendarWeek />, title: 'Every account, one calendar', body: 'All eight networks on a single grid, so you can see what is going out where without switching apps.' },
  { icon: <Funnel />,       title: 'Filter what you see', body: 'Narrow to one account, one network, or one status when the month gets busy.' },
  { icon: <Clock />,        title: 'Gaps you can actually see', body: 'Empty days are obvious on a calendar in a way they never are in a list. That is the point of planning visually.' },
  { icon: <People />,       title: 'Share with clients and teams', body: 'Send a read-only view so people can see the plan without touching it.' },
  { icon: <CheckCircleFill />, title: 'Status at a glance', body: 'Scheduled, published and failed posts are distinct, so a failure is caught the same day rather than next month.' },
]

const FAQS = [
  ['Does dragging a post change when it publishes?',
   'Yes. Move a post to a different day and its scheduled time updates automatically.'],
  ['Can I see all my social accounts on one calendar?',
   'Yes. Instagram, Facebook, Threads, X, LinkedIn, TikTok, Pinterest and YouTube all appear on the same grid, and you can filter to one account when you need to.'],
  ['What views are available?',
   'Month, week, day and agenda. Month is the default.'],
  ['Can I share the calendar with a client?',
   'Yes — share a read-only view so they can see the plan without editing it.'],
  ['Can I tell which posts failed to publish?',
   'Yes. Failed posts are marked distinctly on the calendar so you can catch and repost them.'],
  ['Can I plan a whole month at once?',
   'Yes. You can add posts to the calendar directly, or upload a month in one go with bulk posting.'],
  ['Is the calendar included in every plan?',
   'Yes, on all plans from $9/month. Every plan starts with a 14-day free trial.'],
]

export default function CalendarView () {
  return (
    <>
      {/* ───────── HERO ───────── */}
      <section className='full-bleed py-5 bg-primary-soft'>
        <Container fluid='lg'>
          <Row className='align-items-center g-5'>
            <Col md={6}>
              <h1 className='display-5 fw-bold mb-3'>
                Drag-and-Drop Social Media Calendar
              </h1>
              <p className='lead mb-4'>
                See every scheduled post across all eight networks on one calendar.
                Move a post by dragging it. Spot the gaps before your audience does.
              </p>
              <Button variant='primary' size='lg' href='/register' className='me-2 mb-2'>
                Start free trial
              </Button>
              <Button variant='outline-primary' size='lg' href='/bulk-posting' className='mb-2'>
                Fill it in one upload
              </Button>
            </Col>
            <Col md={6}>
              {/* The real product, not a mock and not stock. This screenshot shows
                  the Status/Platform/Account filters, the Month/Week/Day/Agenda
                  toggles and actual scheduled posts with thumbnails — it proves
                  every claim this page makes, which no illustration can. Encoded
                  at WebP q92: screenshots carry text and show artefacts at the
                  q80 used for photographs. */}
              <Image
                src={calendarShot}
                alt='The Greatly Social content calendar in month view, filtered by platform and account, showing scheduled posts with thumbnails'
                fluid
                className='shadow-card rounded-3 border'
              />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── DEFINITION (snippet target) ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <p className='fs-5'>
            A <strong>social media content calendar</strong> is a single view of everything
            you have scheduled, laid out by date rather than buried in a list. It answers the
            two questions a list never does: what is going out this week, and where are the
            gaps?
          </p>
          <p className='text-muted fs-5 mb-0'>
            Once your posting is spread across several networks, a calendar stops being a
            nice-to-have. It is the only way to see your actual cadence.
          </p>
        </Container>
      </section>

      {/* ───────── FEATURES ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg'>
          <Row className='justify-content-center mb-4'>
            <Col lg={8} className='text-center'>
              <h2 className='fw-bold mb-3'>Everything on one grid</h2>
            </Col>
          </Row>
          <Row className='g-4'>
            {FEATURES.map(({ icon, title, body }) => (
              <Col md={4} key={title}>
                <Card className='h-100 shadow-card'>
                  <Card.Body>
                    <div className='fs-3 text-primary mb-2'>{icon}</div>
                    <Card.Title className='fw-semibold h6'>{title}</Card.Title>
                    <Card.Text className='text-muted small'>{body}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ───────── DRAG AND DROP ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 820 }}>
          <h2 className='fw-bold mb-3'>Rescheduling should take one second</h2>
          <p className='text-muted fs-5'>
            Most tools make you open a post, find the date field, change it, and save. On a
            calendar you drag it. A launch slipping by a week becomes a few seconds of
            dragging rather than twenty edits.
          </p>
          <p className='text-muted fs-5 mb-0'>
            Times move with the post, so a post scheduled for 9:30am stays at 9:30am on its
            new day unless you change it.
          </p>
        </Container>
      </section>

      {/* ───────── VIEWS ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 820 }}>
          <h2 className='fw-bold mb-4'>Four ways to look at the same plan</h2>
          <div className='table-responsive'>
            <table className='table table-bordered bg-white align-middle'>
              <thead className='table-light'>
                <tr><th style={{ width: 120 }}>View</th><th>Best for</th></tr>
              </thead>
              <tbody>
                {VIEWS.map(([v, b]) => (
                  <tr key={v}><td className='fw-semibold'>{v}</td><td className='text-muted'>{b}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </Container>
      </section>

      {/* ───────── WORKFLOW ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4'>How a month usually comes together</h2>
          <ol className='fs-5 text-muted ps-3'>
            <li className='mb-3'><strong>Fill the calendar.</strong> Add posts directly, or <a href='/bulk-posting'>upload a month in one go</a>.</li>
            <li className='mb-3'><strong>Look for the holes.</strong> Empty days are obvious. Fill them or decide they are deliberate.</li>
            <li className='mb-3'><strong>Move things around.</strong> Drag posts until the cadence looks right across networks.</li>
            <li><strong>Share it.</strong> Send a read-only view to whoever needs to sign it off.</li>
          </ol>
        </Container>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className='py-5 bg-light'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4 text-center'>Calendar FAQs</h2>
          <Accordion flush defaultActiveKey='0'>
            {FAQS.map(([q, a], i) => (
              <Accordion.Item eventKey={String(i)} key={i}>
                <Accordion.Header>{q}</Accordion.Header>
                <Accordion.Body className='text-muted'>{a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* ───────── CTA ───────── */}
      <section className='full-bleed py-5 text-center bg-primary text-white'>
        <div className='mx-auto px-4' style={{ maxWidth: 720 }}>
          <h2 className='fw-bold mb-3'>At Greatly Social you can see your whole month at once</h2>
          <p className='lead mb-4'>Greatly Social puts every scheduled post across all eight networks on one calendar. Free for 14 days, plans from $9/month.</p>
          <Button variant='light' size='lg' href='/register' className='me-2 mb-2'>
            Start free trial
          </Button>
          <Button variant='outline-light' size='lg' href='/plans' className='mb-2'>
            See plans
          </Button>
        </div>
      </section>
    </>
  )
}
